'use client';

import { Button, CheckboxField } from '@petsmart-ui/sparky';
import { useEffect, useRef, useState } from 'react';
import { useCurrentRefinements, useRefinementList } from 'react-instantsearch';

import type { FindStoreNearMeProps } from '@app/plp/components/FindStoreNearMe';
import '@app/plp/components/FindStoreNearMe/FindStoreNearMe.scss';
import { closeButtonDropdown } from '@app/plp/utils/closeButtonDropdown';
import { FILTERS, configDynamicFacets } from '@libs/algolia';
import {
    dataLayerPush,
    trackApplyFacetedFilters,
    trackPLPFilters,
} from '@libs/analytics';
import { RenderOnClient, clsx, usePageLayout } from '@libs/common-next';
import type { CurrentRefinementsConnectorParamsItem } from 'instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements';

export const getCountStore = (
    items: CurrentRefinementsConnectorParamsItem[]
) => {
    const inventory = items.find(
        item => item.attribute === FILTERS.SHOP_BY_STORE.ATTRIBUTE
    );
    const [refinement] = inventory?.refinements ?? [];
    return refinement?.count ?? 0;
};

export default function FindStoreNearMe({
    openStoreModal,
    showActions,
    clearShop,
    pageId,
    handleIsStoreFilterApplied,
}: Readonly<FindStoreNearMeProps>) {
    const {
        selectedStore: { store },
    } = usePageLayout();

    const { refine } = useRefinementList({
        attribute: FILTERS.SHOP_BY_STORE.ATTRIBUTE,
    });
    const { items: refinedItems } = useCurrentRefinements({
        includedAttributes: [FILTERS.SHOP_BY_STORE.ATTRIBUTE],
    });

    const isSelectingNewStore = useRef<string>(''); // Used to track if we have opened and selected a new store (value: 'openModal' || '')
    const [isChecked, setIsChecked] = useState<boolean>(false);

    const openModal = () => {
        closeButtonDropdown();
        openStoreModal();
        isSelectingNewStore.current = 'openModal';
    };

    const handleOnCheckboxChange = (val: boolean) => {
        setIsChecked(val);

        if (store && val) {
            refine(store.StoreNumber.toString());

            if (store.Name) {
                const value = store.Name;
                const title =
                    configDynamicFacets[FILTERS.SHOP_BY_STORE.ATTRIBUTE]?.title;

                dataLayerPush({
                    event: FILTERS.EVENT.VALUE,
                    filter_type: title,
                    filter_selection: value,
                    results: getCountStore(refinedItems),
                });
                trackPLPFilters(title, value);
                trackApplyFacetedFilters(title, value, pageId);
            }
        } else {
            clearShop();
        }

        isSelectingNewStore.current = '';
    };

    // This effect is used to update search when we select a new store
    useEffect(() => {
        if (store?.StoreNumber) {
            if (refinedItems.length > 0) {
                clearShop();
                refine(store?.StoreNumber.toString());
                setIsChecked(true);
            } else if (
                isSelectingNewStore.current === 'openModal' &&
                refinedItems.length === 0
            ) {
                clearShop();
                refine(store?.StoreNumber.toString());
                setIsChecked(true);
                isSelectingNewStore.current = '';
            } else if (
                isSelectingNewStore.current === '' &&
                refinedItems.length === 0
            ) {
                setIsChecked(false);
            }
        }
    }, [store, clearShop, refine, refinedItems.length]);

    useEffect(() => {
        const hasStoreFilter = refinedItems.find(
            (e: CurrentRefinementsConnectorParamsItem) =>
                e.attribute === FILTERS.SHOP_BY_STORE.ATTRIBUTE
        );
        if (!hasStoreFilter) {
            setIsChecked(false);
        }
    }, [refinedItems]);

    useEffect(() => {
        if (handleIsStoreFilterApplied) {
            handleIsStoreFilterApplied(isChecked);
        }
    }, [isChecked, handleIsStoreFilterApplied]);

    const classNames = clsx(
        'find-store-near-me',
        isChecked && 'filter-refinement-store--selected'
    );

    const classNamesStore = clsx(
        'find-store-near-me__selections',
        !store && 'find-store-near-me__selections-no-store'
    );

    return (
        <RenderOnClient>
            <div className={classNames}>
                <div className={classNamesStore}>
                    {!store ? (
                        <Button
                            data-testid="find-store-near-me-button"
                            text={FILTERS.SHOP_BY_STORE.NEAR_ME}
                            variant="link"
                            onClick={openModal}
                        />
                    ) : (
                        <div
                            className="find-store-near-me__filter"
                            data-testid="find-store-near-me-filter"
                        >
                            <CheckboxField
                                className="sparky-u-margin-bottom-lg "
                                hideLegend
                                label={FILTERS.SHOP_BY_STORE.CHOOSE_STORE}
                            >
                                {/* the 'key' here is a hack so that we can control the visual checkmark with isChecked */}
                                {/* https://stackoverflow.com/questions/43476729/react-checkbox-does-not-update */}
                                <CheckboxField.Item
                                    key={`shop-checkbox${
                                        isChecked ? '--checked' : ''
                                    }`}
                                    text={store.Name}
                                    onChange={handleOnCheckboxChange}
                                    checked={isChecked}
                                />
                            </CheckboxField>
                            <Button
                                text={FILTERS.SHOP_BY_STORE.CHANGE_STORE}
                                variant="link"
                                onClick={openModal}
                            />
                        </div>
                    )}
                </div>
                {showActions && (
                    <div className="filter-refinement-list__actions">
                        <Button
                            data-testid="filter-refinement-list-store-button-reset-values"
                            fullWidth
                            onClick={clearShop}
                            text={FILTERS.SHOP_BY_STORE.CLEAR}
                            type="button"
                            variant="link"
                            className="filter-refinement-list__actions--reset sparky-u-padding-sm sparky-u-margin-bottom-sm"
                            disabled={!isChecked}
                        />
                        <Button
                            data-testid="filter-refinement-list-store-button-close"
                            fullWidth
                            onClick={closeButtonDropdown}
                            text={FILTERS.SHOP_BY_STORE.VIEW_RESULTS}
                            type="button"
                            variant="primary"
                            className="filter-refinement-list__actions--view"
                        />
                    </div>
                )}
            </div>
        </RenderOnClient>
    );
}
